import React from "react";
import { Models } from "@hex-insights/app-modules";
import { Button, Column, If, Row } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	FileUploadField,
	Form,
	ImageEditorOptions,
	RadioField,
	SubFormField,
	SubFormRenderProps,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import { RadioTextInput } from "../../../Components/RadioTextInput";
import { PersonFormat } from "../../Format";
import {
	AddressFormValues,
	EmailAddressFormValues,
	PersonCustomFormValues,
	PersonFormValues,
	PhoneNumberFormValues,
	UserFormValues,
} from "../../FormUtilities";
import { NameOrderField } from "../_General";
import { AddressForm, AddressSelectField } from "../Address";
import { CountrySelectField } from "../Country";
import { EmailAddressForm, EmailAddressSelectField } from "../EmailAddress";
import { EmployeeSelectField } from "../Employee";
import { GuardianSelectField } from "../Guardian";
import { LanguageSelectField } from "../Language";
import { PhoneNumberForm, PhoneNumberSelectField } from "../PhoneNumber";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import { UserForm, UserSelectField } from "../User";

/**
 * Generic props for fields of the Person model.
 */
type FieldProps<K extends keyof PersonFormValues.Base = keyof PersonFormValues.Base> = BaseFieldProps<
	Pick<PersonFormValues.Base, K>
>;

/**
 * Generic props for fields of the Person model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof PersonFormValues.Create = keyof PersonFormValues.Create> = BaseFieldProps<
	Pick<PersonFormValues.Create, K>
>;

/**
 * Generic props for fields of the Person model that only appear in the custom create form.
 */
type CustomCreateFieldProps<K extends keyof PersonCustomFormValues.Create = keyof PersonCustomFormValues.Create> =
	BaseFieldProps<Pick<PersonCustomFormValues.Create, K>>;

/**
 * Generic props for fields of the Person model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof PersonFormValues.Detail = keyof PersonFormValues.Detail> = BaseFieldProps<
	Pick<PersonFormValues.Detail, K>
>;

/**
 * Field component for the `name` field of the Person model.
 */
export function Name({ formState, ...props }: DetailFieldProps<"name">) {
	return <TextField formState={formState} name="name" {...props} />;
}

/**
 * Field component for the `publicName` field of the Person model.
 */
export function PublicName({ formState, ...props }: DetailFieldProps<"publicName">) {
	return <TextField formState={formState} name="publicName" {...props} />;
}

/**
 * Field component for the `givenName` field of the Person model.
 */
export function GivenName({ formState, ...props }: FieldProps<"givenName">) {
	return <TextField formState={formState} name="givenName" {...props} />;
}

/**
 * Field component for the `middleName` field of the Person model.
 */
export function MiddleName({ formState, ...props }: FieldProps<"middleName">) {
	return <TextField formState={formState} name="middleName" optional {...props} />;
}

/**
 * Field component for the `familyName` field of the Person model.
 */
export function FamilyName({ formState, ...props }: FieldProps<"familyName">) {
	return <TextField formState={formState} name="familyName" {...props} />;
}

/**
 * Field component for the `nameOrder` field of the Person model.
 */
export function NameOrder(props: FieldProps<"nameOrder" | "givenName" | "familyName" | "nickname">) {
	const { givenName, familyName, nickname } = props.formState.formValues;

	return (
		<NameOrderField {...props} name="nameOrder" givenName={givenName} familyName={familyName} nickname={nickname} />
	);
}

/**
 * Field component for the `nickname` field of the Person model.
 */
export function Nickname({
	formState,
	hint = "Nickname or preferred name, if different from their given name.",
	...props
}: FieldProps<"nickname">) {
	return <TextField formState={formState} name="nickname" optional hint={hint} {...props} />;
}

/**
 * Field component for the `alternateNames` field of the Person model.
 */
export function AlternateNames({
	formState,
	hint = "Alternate names are used to help with search. This field can include text in any format.",
	...props
}: FieldProps<"alternateNames">) {
	return <TextField formState={formState} name="alternateNames" optional hint={hint} {...props} />;
}

const imageEditorOptions: ImageEditorOptions = {
	aspect: 1,
	circularCrop: true,
	requireCrop: true,
};

/**
 * Field component for the `nameOrder` field of the Person model.
 */
export function PublicNameFormat({
	formState,
	...props
}: FieldProps<"publicNameFormat" | "nameOrder" | "givenName" | "familyName">) {
	const { nameOrder, givenName, familyName } = formState.formValues;

	const options = React.useMemo(() => {
		return PersonFormValues.publicNameFormatOptions.map((e) => ({
			...e,
			label: PersonFormat.publicNameFromFields({
				givenName: givenName || "Given",
				familyName: familyName || "Family",
				nameOrder,
				publicNameFormat: e.value,
			}),
		}));
	}, [givenName, familyName, nameOrder]);

	return (
		<RadioField formState={formState} name="publicNameFormat" options={options} blankValue={null} optional {...props} />
	);
}

/**
 * Field component for the `image` field of the Person model.
 */
export function Image({ formState, url, ...props }: FieldProps<"imageKey" | "imageContentType"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "Image");

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="imageKey"
			contentTypeFieldName="imageContentType"
			imageEditorOptions={imageEditorOptions}
			url={url}
			optional
			{...props}
		/>
	);
}

/**
 * Field component for the `gender` field of the Person model.
 */
export function Gender({ formState, ...props }: FieldProps<"gender">) {
	return <TextField formState={formState} name="gender" optional Input={GenderInput} {...props} />;
}

const genderOptions = [{ value: "Male" }, { value: "Female" }];

function GenderInput(props: TextFieldInputProps) {
	return <RadioTextInput options={genderOptions} {...props} className={undefined} />;
}

/**
 * Field component for the `dateOfBirth` field of the Person model.
 */
export function DateOfBirth({ formState, ...props }: FieldProps<"dateOfBirth">) {
	return <DateTimeField formState={formState} name="dateOfBirth" optional precision="day" {...props} />;
}

/**
 * Field component for the `englishLanguageFluency` field of the Person model.
 */
export function EnglishLanguageFluency({ formState, ...props }: FieldProps<"englishLanguageFluency">) {
	return (
		<RadioField
			formState={formState}
			name="englishLanguageFluency"
			options={PersonFormValues.englishLanguageFluencyOptions}
			blankValue={null}
			optional
			{...props}
		/>
	);
}

/**
 * Field component for the `occupation` field of the Person model.
 */
export function Occupation({ formState, ...props }: FieldProps<"occupation">) {
	return <TextField formState={formState} name="occupation" optional {...props} />;
}

/**
 * Field component for the `nationalIdentificationNumber` field of the Person model.
 */
export function NationalIdentificationNumber({ formState, ...props }: FieldProps<"nationalIdentificationNumber">) {
	return <TextField formState={formState} name="nationalIdentificationNumber" optional {...props} />;
}

/**
 * Field component for the `governmentDocumentSeries` field of the Person model.
 */
export function GovernmentDocumentSeries({ formState, ...props }: FieldProps<"governmentDocumentSeries">) {
	return <TextField formState={formState} name="governmentDocumentSeries" optional {...props} />;
}

/**
 * Field component for the `governmentDocumentNumber` field of the Person model.
 */
export function GovernmentDocumentNumber({ formState, ...props }: FieldProps<"governmentDocumentNumber">) {
	return <TextField formState={formState} name="governmentDocumentNumber" optional {...props} />;
}

/**
 * Field component for the `governmentDocumentIssuedBy` field of the Person model.
 */
export function GovernmentDocumentIssuedBy({ formState, ...props }: FieldProps<"governmentDocumentIssuedBy">) {
	return <TextField formState={formState} name="governmentDocumentIssuedBy" optional {...props} />;
}

export namespace Addresses {
	export type SelectProps = FieldProps<"addressIDs"> &
		Omit<AddressSelectField.MultiSelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `addresses` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <AddressSelectField.MultiSelect formState={formState} name="addressIDs" {...props} />;
	}

	/**
	 * SubForm field component for the `addresses` edge of the TestModel model.
	 */
	export function SubForm({
		formState,
		noHeader = true,
		maxItems,
		style,
		...props
	}: CustomCreateFieldProps<"addresses"> & { maxItems?: number }) {
		return (
			<SubFormField
				formState={formState}
				name="addresses"
				blankItem={AddressFormValues.blankItemInPerson}
				noHeader={noHeader}
				maxItems={maxItems}
				style={{ width: "45rem", ...style }}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState, onRemoveClick }: SubFormRenderProps<AddressFormValues.CreateInPerson>) {
		return (
			<Row justify="spaced-start">
				<Form.Column style={{ minWidth: 0 }}>
					<AddressForm.CreateFieldsInPerson formState={formState} />
				</Form.Column>

				<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
					<Icon.Trash size={14} className="block" />
				</Button>
			</Row>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		numItems,
		canAddItem,
	}: AddSubFormButtonRenderProps<Pick<PersonCustomFormValues.Create, "addresses">, AddressFormValues.CreateInPerson>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					{numItems === 0 ? "Add Address" : "Add Another Address"}
				</Button>
			</If>
		);
	};
}

export namespace EmailAddresses {
	export type SelectProps = DetailFieldProps<"emailAddressIDs"> &
		Omit<EmailAddressSelectField.MultiSelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `emailAddresses` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <EmailAddressSelectField.MultiSelect formState={formState} name="emailAddressIDs" {...props} />;
	}

	/**
	 * SubForm field component for the `emailAddresses` edge of the TestModel model.
	 */
	export function SubForm({ formState, noHeader, style, ...props }: CreateFieldProps<"emailAddresses">) {
		return (
			<SubFormField
				formState={formState}
				name="emailAddresses"
				blankItem={EmailAddressFormValues.blankItemInPerson}
				noHeader={noHeader ?? formState.formValues.emailAddresses.length > 0}
				style={{ width: "45rem", ...style }}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState, onRemoveClick }: SubFormRenderProps<EmailAddressFormValues.CreateInPerson>) {
		return (
			<Row justify="spaced-start">
				<EmailAddressForm.CreateFieldsInPerson formState={formState} />

				<Column justify="center">
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						<Icon.Trash size={14} className="block" />
					</Button>
				</Column>
			</Row>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		numItems,
	}: AddSubFormButtonRenderProps<
		Pick<PersonFormValues.Create, "emailAddresses">,
		EmailAddressFormValues.CreateInPerson
	>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				{numItems === 0 ? "Add Email Address" : "Add Another Email Address"}
			</Button>
		);
	};
}

export namespace Employee {
	export type SelectProps = FieldProps<"employeeID"> &
		Omit<EmployeeSelectField.SelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `employee` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <EmployeeSelectField.Select formState={formState} name="employeeID" optional {...props} />;
	}
}

export namespace Guardian {
	export type SelectProps = FieldProps<"guardianID"> &
		Omit<GuardianSelectField.SelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `guardian` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <GuardianSelectField.Select formState={formState} name="guardianID" optional {...props} />;
	}
}

export namespace Languages {
	export type SelectProps = FieldProps<"languageIDs"> &
		Omit<LanguageSelectField.MultiSelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `languages` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <LanguageSelectField.MultiSelect formState={formState} name="languageIDs" {...props} />;
	}
}

export namespace Nationalities {
	export type SelectProps = FieldProps<"nationalityIDs"> &
		Omit<CountrySelectField.MultiSelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `nationalities` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CountrySelectField.MultiSelect formState={formState} name="nationalityIDs" {...props} />;
	}
}

export namespace PhoneNumbers {
	export type SelectProps = DetailFieldProps<"phoneNumberIDs"> &
		Omit<PhoneNumberSelectField.MultiSelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `phoneNumbers` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <PhoneNumberSelectField.MultiSelect formState={formState} name="phoneNumberIDs" {...props} />;
	}

	/**
	 * SubForm field component for the `phoneNumbers` edge of the TestModel model.
	 */
	export function SubForm({ formState, noHeader, style, ...props }: CreateFieldProps<"phoneNumbers">) {
		return (
			<SubFormField
				formState={formState}
				name="phoneNumbers"
				blankItem={PhoneNumberFormValues.blankItemInPerson}
				noHeader={noHeader ?? formState.formValues.phoneNumbers.length > 0}
				style={{ width: "45rem", ...style }}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({ formState, onRemoveClick }: SubFormRenderProps<PhoneNumberFormValues.CreateInPerson>) {
		return (
			<Row justify="spaced-start">
				<PhoneNumberForm.CreateFieldsInPerson formState={formState} />

				<Column justify="center">
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						<Icon.Trash size={14} className="block" />
					</Button>
				</Column>
			</Row>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		numItems,
	}: AddSubFormButtonRenderProps<Pick<PersonFormValues.Create, "phoneNumbers">, PhoneNumberFormValues.CreateInPerson>) {
		return (
			<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
				{numItems === 0 ? "Add Phone Number" : "Add Another Phone Number"}
			</Button>
		);
	};
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<StudentSelectField.SelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `student` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" optional {...props} />;
	}
}

export namespace User {
	export type SelectProps = DetailFieldProps<"userID"> &
		Omit<UserSelectField.SelectProps<PersonFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `user` edge of the Person model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="userID" optional {...props} />;
	}

	/**
	 * SubForm field component for the `user` edge of the TestModel model.
	 */
	export function SubForm({
		formState,
		hint = "Grant portal access to this person. This can be done later.",
		...props
	}: CreateFieldProps<"user">) {
		return (
			<SubFormField
				formState={formState}
				name="user"
				blankItem={UserFormValues.blankItemInPerson}
				maxItems={1}
				hint={hint}
				formattedBlank=""
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		canRemoveItem,
	}: SubFormRenderProps<UserFormValues.CreateInPerson>) {
		return (
			<Form.Column>
				<If condition={canRemoveItem}>
					<Button variant="tertiary" size="small" onClick={onRemoveClick} disabled={formState.disabled}>
						Remove User
					</Button>
				</If>
				<UserForm.CreateFieldsInPerson formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		canAddItem,
	}: AddSubFormButtonRenderProps<Pick<PersonFormValues.Create, "user">, UserFormValues.CreateInPerson>) {
		return (
			<If condition={canAddItem}>
				<Button variant="tertiary" size="small" onClick={onClick} disabled={disabled}>
					Add User
				</Button>
			</If>
		);
	};
}
